import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import * as Sentry from '@sentry/react';
import { EventEmitter } from '../../lib/events';
import { Operation, isApolloError } from '@apollo/client';
import { GraphQLError } from 'graphql';

type Events = {
  unauthenticated: () => void;
};

export const emitter = new EventEmitter<Events>();

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach((error) => {
      if (handleGlobalError(error)) {
        return;
      }

      // this is in fact NOT an error object despite ts type so use captureMessage instead
      Sentry.withScope((scope) => {
        scope.setExtra('error', error);
        scope.setExtra('operation', {
          name: operation.operationName,
          variables: operation.variables,
        });
        Sentry.captureMessage(
          `GraphQL error: ${error.message}`,
          Sentry.Severity.Error,
        );
      });
      console.error(`[GraphQL error] ${operation.operationName}:`, error);
    });
  }
  if (networkError) {
    if (isSubscription(operation)) {
      // In subscriptions graphql errors can be networkError
      if (handleGlobalError(networkError as GraphQLError)) {
        return;
      }
    }

    Sentry.withScope((scope) => {
      scope.setExtra('operation', {
        name: operation.operationName,
        variables: operation.variables,
      });
      // Sometimes even networkError is not an error object
      if (networkError instanceof Error) {
        Sentry.captureException(networkError);
      } else {
        scope.setExtra('error', networkError);
        Sentry.captureMessage(
          `Network error: ${(networkError as any).message}`,
          Sentry.Severity.Error,
        );
      }
    });
    console.error(`[Network error] ${operation.operationName}:`, networkError);
  }
});

export default errorLink;

const handleGlobalError = (error: GraphQLError) => {
  if (error.extensions?.code === 'UNAUTHENTICATED') {
    emitter.emit('unauthenticated');
    return true;
  }
  return false;
};

const HANDLED_GLOBAL_CODES = ['UNAUTHENTICATED'];

export const isErrorGloballyHandled = (error: Error) => {
  if (isApolloError(error)) {
    const { graphQLErrors } = error;
    if (
      graphQLErrors.some(
        (e) => HANDLED_GLOBAL_CODES.indexOf(e.extensions?.code) !== -1,
      )
    ) {
      return true;
    }
  }
  return false;
};

const isSubscription = (operation: Operation) => {
  const definition = getMainDefinition(operation.query);
  return (
    definition.kind === 'OperationDefinition' &&
    definition.operation === 'subscription'
  );
};
